import React from 'react';

interface Props {
  barrelAngle: number; // radians, 0 = horizontal right
  firing: boolean;
}

const TankSVG: React.FC<Props> = ({ barrelAngle, firing }) => {
  // pivot of barrel relative to SVG origin
  const bpx = 68, bpy = 52;
  const barrelLen = 52;
  const bx2 = bpx + Math.cos(barrelAngle) * barrelLen;
  const by2 = bpy + Math.sin(barrelAngle) * barrelLen;

  return (
    <svg viewBox="0 0 160 110" width="160" height="110" className="tank-svg">
      {/* Shadow */}
      <ellipse cx="80" cy="105" rx="60" ry="6" fill="rgba(0,0,0,0.25)" />

      {/* === TRACKS === */}
      {/* Track body */}
      <rect x="14" y="68" width="132" height="28" rx="14" fill="#2a2a1e" />
      {/* Track highlight */}
      <rect x="16" y="70" width="128" height="10" rx="5" fill="#3a3a2c" />
      {/* Track links */}
      {Array.from({ length: 10 }).map((_, i) => (
        <rect key={i} x={18 + i * 13} y="68" width="10" height="28" rx="2"
          fill="none" stroke="#1a1a10" strokeWidth="1.5" />
      ))}
      {/* Road wheels */}
      {[30, 52, 74, 96, 118].map((cx, i) => (
        <g key={i}>
          <circle cx={cx} cy="82" r="11" fill="#3d3d2a" stroke="#222" strokeWidth="1.5" />
          <circle cx={cx} cy="82" r="6"  fill="#2a2a1c" />
          <circle cx={cx} cy="82" r="2.5" fill="#4a4a35" />
        </g>
      ))}
      {/* Drive sprocket (front) */}
      <circle cx="136" cy="78" r="10" fill="#3d3d2a" stroke="#222" strokeWidth="1.5" />
      <circle cx="136" cy="78" r="5"  fill="#2a2a1c" />
      {/* Idler (rear) */}
      <circle cx="24" cy="78" r="8" fill="#3d3d2a" stroke="#222" strokeWidth="1.5" />
      <circle cx="24" cy="78" r="4"  fill="#2a2a1c" />

      {/* === HULL === */}
      {/* Hull body */}
      <path d="M22 40 L138 40 L146 58 L148 68 L12 68 L14 58 Z" fill="#5a6438" />
      {/* Hull top bevel */}
      <path d="M22 40 L138 40 L133 44 L27 44 Z" fill="#6b7848" />
      {/* Hull armor plate lines */}
      <line x1="40" y1="44" x2="38" y2="68" stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
      <line x1="80" y1="44" x2="80" y2="68" stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
      <line x1="120" y1="44" x2="122" y2="68" stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
      {/* Hull side highlight */}
      <path d="M22 40 L27 44 L27 56 L22 52 Z" fill="rgba(255,255,255,0.08)" />
      {/* Hull exhaust port */}
      <rect x="18" y="54" width="8" height="6" rx="2" fill="#333" />
      <rect x="18" y="55" width="8" height="2" rx="1" fill="#222" />

      {/* === TURRET === */}
      {/* Turret base ring */}
      <ellipse cx="72" cy="40" rx="35" ry="7" fill="#3e4a22" />
      {/* Turret body */}
      <path d="M38 36 Q40 18 72 16 Q104 18 106 36 L106 40 L38 40 Z" fill="#4e5e2a" />
      {/* Turret armor weld lines */}
      <path d="M52 18 L50 36" stroke="rgba(0,0,0,0.18)" strokeWidth="1.5" fill="none" />
      <path d="M72 16 L72 36" stroke="rgba(0,0,0,0.18)" strokeWidth="1.5" fill="none" />
      <path d="M92 18 L94 36" stroke="rgba(0,0,0,0.18)" strokeWidth="1.5" fill="none" />
      {/* Turret highlight */}
      <path d="M48 20 Q72 15 96 20 L92 22 Q72 17 52 22 Z" fill="rgba(255,255,255,0.1)" />
      {/* Commander hatch */}
      <ellipse cx="72" cy="18" rx="10" ry="4" fill="#3a4520" stroke="#2a3518" strokeWidth="1" />
      <ellipse cx="72" cy="17" rx="6"  ry="2.5" fill="#2a3015" />
      {/* Periscope */}
      <rect x="69" y="11" width="6" height="8" rx="1.5" fill="#2a2a1a" />
      <rect x="70" y="12" width="4" height="3" rx="0.5" fill="rgba(120,180,255,0.5)" />

      {/* === BARREL === */}
      <g>
        {/* Barrel mount */}
        <circle cx={bpx} cy={bpy} r="7" fill="#3a4220" stroke="#2a3015" strokeWidth="1.5" />
        {/* Barrel shadow */}
        <line
          x1={bpx} y1={bpy}
          x2={bpx + Math.cos(barrelAngle + 0.05) * (barrelLen + 4)}
          y2={bpy + Math.sin(barrelAngle + 0.05) * (barrelLen + 4)}
          stroke="rgba(0,0,0,0.3)" strokeWidth="10" strokeLinecap="round"
        />
        {/* Main barrel */}
        <line
          x1={bpx} y1={bpy}
          x2={bx2} y2={by2}
          stroke="#2e3818" strokeWidth="9" strokeLinecap="round"
        />
        {/* Barrel highlight */}
        <line
          x1={bpx} y1={bpy - 1}
          x2={bx2} y2={by2 - 1}
          stroke="#3e4a24" strokeWidth="4" strokeLinecap="round"
        />
        {/* Muzzle brake */}
        <circle cx={bx2} cy={by2} r="5.5" fill="#252e12" stroke="#1a2008" strokeWidth="1" />
        <circle cx={bx2} cy={by2} r="3"   fill="#1a2008" />
        {/* Muzzle flash */}
        {firing && (
          <g>
            <circle cx={bx2 + Math.cos(barrelAngle) * 8} cy={by2 + Math.sin(barrelAngle) * 8} r="12" fill="rgba(255,200,50,0.9)" />
            <circle cx={bx2 + Math.cos(barrelAngle) * 8} cy={by2 + Math.sin(barrelAngle) * 8} r="7"  fill="rgba(255,120,20,1)" />
            <circle cx={bx2 + Math.cos(barrelAngle) * 8} cy={by2 + Math.sin(barrelAngle) * 8} r="4"  fill="white" />
          </g>
        )}
      </g>

      {/* === DETAILS === */}
      {/* Smoke dischargers */}
      <rect x="38" y="20" width="4" height="8" rx="1" fill="#2a2a1a" transform="rotate(-10,40,24)" />
      <rect x="43" y="19" width="4" height="8" rx="1" fill="#2a2a1a" transform="rotate(-5,45,23)" />
      {/* Track guards */}
      <rect x="12" y="60" width="136" height="6" rx="2" fill="#4a5530" />
      {/* Hull front slope */}
      <path d="M138 40 L146 58 L148 68 L140 68 L136 58 Z" fill="#4a5428" />
    </svg>
  );
};

export default TankSVG;
